/**
 * Deck summary exporters (Text Summary + Markdown Summary)
 *
 * Grouped by card type category for human-readable output.
 * Text Summary is re-importable. Markdown is optimized for Reddit/Discord.
 */
import { DECK_FORMAT_LABELS } from '@/lib/deckUtils';
import type { Deck } from '@/types/deck';

import type { ExportableDeckCard } from '../../decks/lib/deckImportExport';
import { groupCardsForSummary } from './summaryGrouping';

// ============================================================================
// Types
// ============================================================================

/** Extended card data for summary export with type_line */
export interface SummaryExportCard extends ExportableDeckCard {
  type_line: string;
}

export interface TextSummaryExportOptions {
  /** Deck metadata for header */
  deck?: Deck;
}

export interface MarkdownSummaryExportOptions {
  /** Deck metadata for header */
  deck?: Deck;
}

// ============================================================================
// Text Summary Export
// ============================================================================

/**
 * Export deck as Text Summary format.
 *
 * Format:
 * ```
 * // Deck: Dragon Tribal EDH
 * // Format: Commander
 * // Cards: 100
 *
 * // Commander (1)
 * 1 Atarka, World Render
 *
 * // Creatures (28)
 * 1 Dragonmaster Outcast
 * ...
 * ```
 *
 * This format is human-readable and can be re-imported.
 */
export function exportTextSummary(cards: SummaryExportCard[], options: TextSummaryExportOptions = {}): string {
  const { deck } = options;
  const lines: string[] = [];

  // Add metadata header
  if (deck) {
    lines.push(`// Deck: ${deck.name}`);
    if (deck.format) {
      lines.push(`// Format: ${DECK_FORMAT_LABELS[deck.format] ?? deck.format}`);
    }
    const totalCards = cards.reduce((sum, c) => sum + c.quantity, 0);
    lines.push(`// Cards: ${totalCards}`);
    lines.push('');
  }

  // Group cards by category
  const categories = groupCardsForSummary(cards);

  for (const category of categories) {
    // Section header: // Category (count)
    lines.push(`// ${category.title} (${category.count})`);

    // Card lines
    for (const card of category.cards) {
      lines.push(`${card.quantity} ${card.card.name}`);
    }

    lines.push('');
  }

  return lines.join('\n').trim();
}

// ============================================================================
// Markdown Summary Export
// ============================================================================

/**
 * Export deck as Markdown Summary format.
 *
 * Format:
 * ```markdown
 * # Dragon Tribal EDH
 *
 * **Format:** Commander | **Cards:** 100
 *
 * ## Commander (1)
 * - 1 Atarka, World Render
 *
 * ## Creatures (28)
 * - 1 Dragonmaster Outcast
 * ...
 * ```
 *
 * Optimized for Reddit, Discord, and other Markdown platforms.
 */
export function exportMarkdownSummary(cards: SummaryExportCard[], options: MarkdownSummaryExportOptions = {}): string {
  const { deck } = options;
  const lines: string[] = [];

  // Title header
  if (deck) {
    lines.push(`# ${deck.name}`);
    lines.push('');

    // Metadata line
    const metaParts: string[] = [];
    if (deck.format) {
      metaParts.push(`**Format:** ${DECK_FORMAT_LABELS[deck.format] ?? deck.format}`);
    }
    const totalCards = cards.reduce((sum, c) => sum + c.quantity, 0);
    metaParts.push(`**Cards:** ${totalCards}`);

    if (metaParts.length > 0) {
      lines.push(metaParts.join(' | '));
      lines.push('');
    }
  }

  // Group cards by category
  const categories = groupCardsForSummary(cards);

  for (const category of categories) {
    // Section header: ## Category (count)
    lines.push(`## ${category.title} (${category.count})`);
    lines.push('');

    // Card lines as list items
    for (const card of category.cards) {
      lines.push(`- ${card.quantity} ${card.card.name}`);
    }

    lines.push('');
  }

  return lines.join('\n').trim();
}
